import React, { useCallback } from 'react';
import T from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useFocusEffect } from '@react-navigation/native';

import { flexDraftChange, resetRequestStatus } from '@dmi/shared/redux/Payments/FlexDraft/actions';
import { PAYMENT_FLOW_TYPE_FLEX_DRAFT } from '@dmi/shared/redux/Payments/FlexDraft/constants';
import makeSelectFlexDraft,
{
  selectChangeRequestBody,
  selectDisclosureDictionary,
  selectRequestStatus,
} from '@dmi/shared/redux/Payments/FlexDraft/selectors';
import makeSelectMain, { getMainClientInfo } from '@dmi/shared/redux/Main/selectors';

import DisclosureScreen from '../../../components/Payments/Disclosure';

const Disclosure = ({
  changeRequestBody,
  clientName,
  customerServicePhoneNumber,
  disclosureDictionary,
  dispatchFlexDraftChange,
  dispatchResetRequestStatus,
  isInFlexDraftFlow,
  loanNumber,
  navigation,
  requestStatusFlexDraftChange,
}) => {
  const {
    summaryButtonLabel,
    summaryHelperText,
    summarySubText,
    summaryText,
  } = disclosureDictionary;
  useFocusEffect(
    useCallback(() => {
      if (!isInFlexDraftFlow) {
        navigation.navigate('Payments');
      }
      return () =>
        dispatchResetRequestStatus({ statusType: 'flexDraftChange' });
    }, [dispatchResetRequestStatus, isInFlexDraftFlow, navigation]),
  );

  return (
    <DisclosureScreen
      clientName={clientName}
      customerServicePhoneNumber={customerServicePhoneNumber}
      isFlexDraft
      navigation={navigation}
      paymentFlowType={PAYMENT_FLOW_TYPE_FLEX_DRAFT}
      summaryProps={{
        error: requestStatusFlexDraftChange === 'failed',
        hasProgressButton: true,
        helperText: summaryHelperText,
        label: summaryButtonLabel,
        loading: requestStatusFlexDraftChange === 'loading',
        onPress: () => dispatchFlexDraftChange({ body: changeRequestBody, loanNumber }),
        subText: summarySubText,
        success: requestStatusFlexDraftChange === 'succeeded',
        text: summaryText,
      }}
    />
  );
};

Disclosure.propTypes = {
  changeRequestBody: T.object.isRequired,
  clientName: T.string.isRequired,
  customerServicePhoneNumber: T.string.isRequired,
  disclosureDictionary: T.object.isRequired,
  dispatchFlexDraftChange: T.func.isRequired,
  dispatchResetRequestStatus: T.func.isRequired,
  isInFlexDraftFlow: T.bool.isRequired,
  loanNumber: T.string.isRequired,
  navigation: T.object.isRequired,
  requestStatusFlexDraftChange: T.string.isRequired,
};

const mapStateToProps = createStructuredSelector({
  /**
   * Store: Main
   */
  clientName: getMainClientInfo('clientName'),
  customerServicePhoneNumber: getMainClientInfo('customerServicePhoneNumber'),
  loanNumber: makeSelectMain('selectedLoan'),
  /**
   * Store: Flex Draft
   */
  // eslint-disable-next-line sort-keys
  changeRequestBody: selectChangeRequestBody(),
  disclosureDictionary: selectDisclosureDictionary(),
  isInFlexDraftFlow: makeSelectFlexDraft('isInFlexDraftFlow'),
  requestStatusFlexDraftChange: selectRequestStatus('flexDraftChange'),
});

const mapDispatchToProps = (dispatch) => ({
  /**
   * Store: Flex Draft
   */
  dispatchFlexDraftChange: (payload) => dispatch(flexDraftChange(payload)),
  dispatchResetRequestStatus: (payload) => dispatch(resetRequestStatus(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withConnect(Disclosure);
